/**
 * 图片组节点：展示多张图片的堆叠效果，支持展开/收起。
 *
 * 堆叠模式：主图缩略卡 + 最多 5 层偏移卡片（主图由 primaryJobId 指定，默认第一张），
 * 点击主图打开全屏灯箱翻页预览。
 * 展开模式：网格缩略图排列所有图片，每张支持：点开预览 / 设为主图 / 复制为新图片
 * 节点 / 收藏 / 下载 / 删除。
 */
import { useState } from 'react';
import { ArrowUpRight, Bookmark, ChevronDown, ChevronUp, Copy, Download, FolderDown, ImageOff, Loader2, RotateCw, Star, Trash2 } from 'lucide-react';
import JSZip from 'jszip';
import type { AgentNodeData, CanvasNode, ImageGroupNodeData } from '../types';
import { NodeWrapper } from './NodeWrapper';
import { NodeToolbarButton } from './NodeToolbar';
import { boardActions, getBoardState, useBoard } from '../state/boardStore';
import { assetStorage } from '../assetStorage';
import { makeNode } from '../utils';
import { toPersistableMediaUrl } from '../utils/persistableMediaUrl';
import { blobUrlToDataUrl } from '../imageStorage';
import { MAX_FALLBACK_DATA_URL_LENGTH } from '../constants';
import toast from '../../../components/Toast';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import { deriveThumbnailUrl } from '../../../utils/imageDisplay';
import { ImageLightbox, type LightboxImage } from '../components/ImageLightbox';
import { extractImageFromGroup } from '../generators/groupOps';
import { isAgentResultGroup } from '../generators/agentUnit';
import { retryAgentItems } from '../generators/agentGenerator';

interface ImageGroupNodeProps {
  node: CanvasNode<ImageGroupNodeData>;
  selected: boolean;
  highlighted: boolean;
}

/** fetch 转 blob 下载，跨域图片也能落盘 */
async function downloadImage(url: string, index: number) {
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = `canvas-group-image-${index + 1}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objUrl);
  } catch {
    window.open(url, '_blank');
  }
}

/** 从 blob MIME 推断图片扩展名（识别不了时兜底 png） */
function extFromMime(mime: string): string {
  if (mime === 'image/jpeg') return 'jpg';
  if (mime === 'image/webp') return 'webp';
  if (mime === 'image/gif') return 'gif';
  return 'png';
}

export function ImageGroupNode({ node, selected, highlighted }: ImageGroupNodeProps) {
  const data = node.data;
  // expanded 单源：直接读写 node.data，撤销/重做后状态一致。
  // 默认展开多图网格（不再默认堆叠主图）；用户手动收起过（显式 false）的保持收起
  const expanded = data.expanded ?? true;
  // 灯箱预览：值为 lightboxImages 内的下标，null = 关闭
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  // 加载失败的槽位（多为旧版本吸收进组的本地 blob 死链）：渲染"图片已失效"占位
  const [failedSlots, setFailedSlots] = useState<Set<number>>(new Set());

  // 优先使用 imageUrls（真实图片），回退到 jobIds 数量占位
  const jobIds = data.jobIds ?? [];
  const imageUrls = (data.imageUrls ?? []).map((u) => resolveBrowserMediaUrl(u));
  // 格子展示用 medium 缩略图省流量（格子尺寸小，1024 上限足够清晰）；
  // 灯箱预览 / 收藏 / 下载 / 取出等操作仍用上面的原图 URL
  const displayUrls = (data.imageUrls ?? []).map((u) => {
    const thumb = u ? deriveThumbnailUrl(u, 'medium') : null;
    return thumb ? resolveBrowserMediaUrl(thumb) : resolveBrowserMediaUrl(u);
  });
  const count = jobIds.length || imageUrls.length || 0;
  // 批量生成进度：整组 processing 时展示 已完成/总数，失败格子在展开模式下单独标红
  const generating = data.status === 'processing';
  const doneCount = (data.statuses ?? []).filter((s) => s === 'completed').length;

  // 所属智能体批次（agent 直连结果组）：失败槽位提供单张重试入口。
  // 订阅 agent 节点对象，其 generating 变化时本组件同步重渲染以禁用按钮
  const owningAgentId = useBoard((s) => {
    const agent = s.nodes.find(
      (n) => n.type === 'agent' && (n.data as AgentNodeData).groupNodeId === node.id
    );
    return agent?.id ?? null;
  });
  const agentGenerating = useBoard((s) => {
    if (!owningAgentId) return false;
    const agent = s.nodes.find((n) => n.id === owningAgentId);
    return !!agent && !!(agent.data as AgentNodeData).generating;
  });

  // 主图排序视图：primaryJobId 对应的图排最前（渲染顺序，不改原数组）。
  // 组没有 jobIds 时用 "#idx:N" 作为合成主键，保证纯 imageUrls 的组也能设主图。
  const primaryKey = (i: number) => jobIds[i] ?? `#idx:${i}`;
  const activePrimary = data.primaryJobId ?? primaryKey(0);
  const orderedIndices = Array.from({ length: count }, (_, i) => i).sort((a, b) => {
    const aPrimary = primaryKey(a) === activePrimary ? 0 : 1;
    const bPrimary = primaryKey(b) === activePrimary ? 0 : 1;
    return aPrimary - bPrimary;
  });

  // 灯箱浏览视图：只含有图可看的格子，按组内自然顺序翻页
  const viewableIndices = Array.from({ length: count }, (_, i) => i).filter((i) => !!imageUrls[i]);
  const lightboxImages: LightboxImage[] = viewableIndices.map((i) => ({
    url: imageUrls[i]!,
    label: `图片 ${i + 1}`,
  }));
  const openPreview = (groupIndex: number) => {
    const pos = viewableIndices.indexOf(groupIndex);
    if (pos < 0 && viewableIndices.length === 0) return;
    setPreviewIndex(pos >= 0 ? pos : 0);
  };

  const toggleExpanded = () => {
    boardActions.updateNodeData(node.id, { expanded: !expanded } as Record<string, unknown>);
  };

  // 打包下载：进行中标志（防重复点击）+ 可打包槽位（有图且未失效）
  const [zipping, setZipping] = useState(false);
  const downloadableIndices = Array.from({ length: count }, (_, i) => i).filter(
    (i) => !!imageUrls[i] && !failedSlots.has(i)
  );

  /** 整组打包下载：逐张 fetch 为 blob 后压成 zip，失效/获取失败的槽位跳过并在结果提示中说明 */
  async function downloadGroupAsZip() {
    if (zipping || downloadableIndices.length === 0) return;
    setZipping(true);
    let ok = 0;
    let skipped = 0;
    try {
      const zip = new JSZip();
      for (const i of downloadableIndices) {
        try {
          const resp = await fetch(imageUrls[i]!);
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const blob = await resp.blob();
          zip.file(`canvas-group-${String(i + 1).padStart(2, '0')}.${extFromMime(blob.type)}`, blob);
          ok++;
        } catch {
          skipped++;
        }
      }
      if (ok === 0) {
        toast.error('打包下载失败：组内图片均无法获取');
        return;
      }
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const objUrl = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `画布图片组-${ok}张.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
      toast.success(skipped > 0 ? `已打包下载 ${ok} 张，${skipped} 张失效或获取失败已跳过` : `已打包下载 ${ok} 张图片`);
    } finally {
      setZipping(false);
    }
  }

  const setPrimary = (index: number) => {
    boardActions.updateNodeData(node.id, { primaryJobId: primaryKey(index) } as Record<string, unknown>);
    toast.success('已设为主图');
  };

  /** 把组内某张图复制为独立图片节点（放在组右侧） */
  const copyToImageNode = (index: number) => {
    // 存原始引用（canonical key / blob URL）而非 resolveBrowserMediaUrl 的展示产物：
    // 展示 URL 带短时效 mediaToken / 会话级 blob 链接，持久化到快照后过期即死链。
    // ImageNode 渲染时会自行 resolve，与新 token 每次会话现取。
    const raw = (data.imageUrls ?? [])[index];
    if (!raw) return;
    const newNode = makeNode(
      'image',
      { x: node.x + node.width + 60, y: node.y + index * 24 },
      {
        imageUrl: raw,
        status: 'completed',
        _title: '组内图片',
      } as Record<string, unknown>
    );
    boardActions.addNode(newNode);
    toast.success('已复制为新图片节点');
  };

  /** 删除组内某张图（同步 jobIds / imageUrls / primaryJobId）；删空后组一并移除，不留空壳。
   *  智能体批次的结果组除外：冻结单元不允许被删空，只能整批删除 */
  const removeImage = (index: number) => {
    const nextJobIds = jobIds.filter((_, i) => i !== index);
    const nextUrls = (data.imageUrls ?? []).filter((_, i) => i !== index);
    if (nextJobIds.length === 0 && nextUrls.length === 0) {
      if (isAgentResultGroup(node.id, getBoardState().nodes)) {
        toast.error('该图片组是智能体批次的结果组，不能删空；如需删除请整体删除该批次');
        return;
      }
      boardActions.removeNodes([node.id]);
      toast.success('组已删空，图片组一并移除');
      return;
    }
    const removedJobId = jobIds[index];
    const patch: Record<string, unknown> = { jobIds: nextJobIds, imageUrls: nextUrls };
    if (removedJobId && data.primaryJobId === removedJobId) {
      patch.primaryJobId = nextJobIds[0] ?? null;
    } else if (typeof data.primaryJobId === 'string' && data.primaryJobId.startsWith('#idx:')) {
      // 纯 imageUrls 组的合成主键按索引指向：删除后重映射，避免主图漂移到别的图或越界
      const oldIdx = Number(data.primaryJobId.slice('#idx:'.length));
      if (oldIdx === index) {
        patch.primaryJobId = nextUrls.length > 0 ? '#idx:0' : null;
      } else if (oldIdx > index) {
        patch.primaryJobId = `#idx:${oldIdx - 1}`;
      }
    }
    boardActions.updateNodeData(node.id, patch);
  };

  return (
    <NodeWrapper
      node={node}
      selected={selected}
      highlighted={highlighted}
      stretchContent
      toolbar={
        count > 0 ? (
          <>
            <NodeToolbarButton onClick={toggleExpanded} tone="primary" title={expanded ? '收起图片组' : '展开图片组'}>
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {expanded ? '收起' : `${count}张`}
            </NodeToolbarButton>
            <NodeToolbarButton
              onClick={() => void downloadGroupAsZip()}
              disabled={zipping || downloadableIndices.length === 0}
              title="整组打包下载为 ZIP（失效图片自动跳过）"
            >
              {zipping ? <Loader2 className="h-3 w-3 animate-spin" /> : <FolderDown className="h-3 w-3" />}
              {zipping ? '打包中' : '打包'}
            </NodeToolbarButton>
          </>
        ) : null
      }
    >
      {count === 0 ? (
        <div className="flex min-h-0 flex-1 items-center justify-center text-xs text-light-muted">空图片组</div>
      ) : (
        <div className="relative flex min-h-0 flex-1 flex-col">
          {/* 图号角标：@图N 引用的稳定编号，常驻左上角 */}
          {typeof data.refNo === 'number' && (
            <span
              className="pointer-events-none absolute -left-1 -top-2 z-20 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
              title={`图号 #${data.refNo}：提示词中可用 @图${data.refNo} 引用整组`}
            >
              #{data.refNo}
            </span>
          )}
          {expanded ? (
        /* 展开模式：网格排列（主图排最前并高亮） */
        <div className="grid min-h-0 flex-1 auto-rows-fr grid-cols-2 gap-1.5 overflow-y-auto">
          {orderedIndices.map((i) => {
            const url = imageUrls[i];
            const displayUrl = displayUrls[i];
            const isPrimary = primaryKey(i) === activePrimary;
            return (
              <div
                key={jobIds[i] ?? i}
                data-no-drag
                className={`group/item relative flex min-h-[4.5rem] cursor-zoom-in items-center justify-center overflow-hidden rounded-md bg-light-hover text-xs ${
                  isPrimary ? 'ring-2 ring-amber-400' : ''
                }`}
                title="点击放大预览"
                onClick={() => url && openPreview(i)}
              >
                {isPrimary && (
                  <span className="absolute left-1 top-1 z-10 flex items-center gap-0.5 rounded-full bg-amber-400 px-1.5 py-0.5 text-[9px] font-semibold text-white shadow">
                    <Star className="h-2.5 w-2.5" /> 主图
                  </span>
                )}
                {url ? (
                  failedSlots.has(i) ? (
                    /* 死链占位：多为旧版本吸收进组的本地 blob 图（刷新后失效且不可恢复），可删除该槽位 */
                    <div className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-md bg-slate-100 text-[10px] text-slate-400">
                      <ImageOff className="h-4 w-4" />
                      <span>图片已失效</span>
                    </div>
                  ) : (
                  <>
                    <img
                      src={displayUrl}
                      alt={`图片 ${i + 1}`}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                      onError={() => setFailedSlots((prev) => (prev.has(i) ? prev : new Set(prev).add(i)))}
                    />
                    {/* 单张快捷操作：设主图 / 复制为节点 / 收藏 / 下载 / 删除（悬停显示） */}
                    <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition group-hover/item:opacity-100">
                      {!isPrimary && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPrimary(i);
                          }}
                          className="grid h-6 w-6 place-items-center rounded-md bg-black/55 text-white backdrop-blur-sm transition hover:bg-amber-500"
                          title="设为主图"
                        >
                          <Star className="h-3 w-3" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          extractImageFromGroup(node.id, i);
                        }}
                        className="grid h-6 w-6 place-items-center rounded-md bg-black/55 text-white backdrop-blur-sm transition hover:bg-emerald-500"
                        title="取出为独立图片节点（专属上游生图节点一并带出）"
                      >
                        <ArrowUpRight className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToImageNode(i);
                        }}
                        className="grid h-6 w-6 place-items-center rounded-md bg-black/55 text-white backdrop-blur-sm transition hover:bg-blue-500"
                        title="复制为新图片节点"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (url) {
                            // 存原始引用的清洗产物（与图片节点收藏一致）；blob: 会话链接转 data URL，并标记所属画布项目
                            void (async () => {
                              const raw = (data.imageUrls ?? [])[i];
                              let content = raw ? (toPersistableMediaUrl(raw) ?? raw) : url;
                              if (content.startsWith('blob:')) {
                                const dataUrl = await blobUrlToDataUrl(content);
                                if (!dataUrl) {
                                  toast.error('收藏失败：本地图片已失效，请重新上传后再收藏');
                                  return;
                                }
                                if (dataUrl.length > MAX_FALLBACK_DATA_URL_LENGTH) {
                                  toast.error('图片过大，无法收藏到素材库');
                                  return;
                                }
                                content = dataUrl;
                              }
                              const result = assetStorage.add({
                                type: 'image',
                                content,
                                projectId: getBoardState().projectId,
                              });
                              toast.success(result.created ? '已收藏到素材库' : '该素材已在素材库中');
                            })();
                          }
                        }}
                        className="grid h-6 w-6 place-items-center rounded-md bg-black/55 text-white backdrop-blur-sm transition hover:bg-black/75"
                        title="收藏到素材库"
                      >
                        <Bookmark className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void downloadImage(url, i);
                        }}
                        className="grid h-6 w-6 place-items-center rounded-md bg-black/55 text-white backdrop-blur-sm transition hover:bg-black/75"
                        title="下载"
                      >
                        <Download className="h-3 w-3" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeImage(i);
                        }}
                        className="grid h-6 w-6 place-items-center rounded-md bg-black/55 text-white backdrop-blur-sm transition hover:bg-red-500"
                        title="从组中删除"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </>
                  )
                ) : (data.statuses ?? [])[i] === 'failed' ? (
                  /* 生成失败：智能体批次的结果组提供单张重试（普通批量组无此入口） */
                  <div className="flex h-full w-full flex-col items-center justify-center gap-1">
                    <span className="text-[10px] text-red-500">生成失败</span>
                    {owningAgentId && (
                      <button
                        type="button"
                        disabled={agentGenerating}
                        onClick={(e) => {
                          e.stopPropagation();
                          void retryAgentItems(owningAgentId, [i]);
                        }}
                        className="flex items-center gap-0.5 rounded-md border border-red-200 px-1.5 py-0.5 text-[9px] text-red-500 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        title="重试该张"
                      >
                        <RotateCw className="h-2.5 w-2.5" /> 重试
                      </button>
                    )}
                  </div>
                ) : (
                  <span className="px-1 text-center text-[10px] leading-tight text-light-muted">
                    {generating ? `生成中 ${doneCount}/${count}` : `图片 ${i + 1}`}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        /* 堆叠模式：主图缩略卡在最上层 + 最多 4 层偏移卡片；点击打开全屏灯箱翻页预览 */
        <div
          data-no-drag
          className="relative h-44 w-full cursor-zoom-in"
          title="点击放大预览"
          onClick={() => imageUrls[orderedIndices[0]] && openPreview(orderedIndices[0])}
        >
          {orderedIndices.slice(0, 5).map((i, layer) => {
            const offset = layer * 4;
            const scale = 1 - layer * 0.02;
            const url = imageUrls[i];
            const displayUrl = displayUrls[i];
            const isPrimary = i === orderedIndices[0];
            return (
              <div
                key={i}
                className="absolute overflow-hidden rounded-lg border border-slate-200 bg-light-hover"
                style={{
                  left: offset,
                  top: offset,
                  right: offset,
                  bottom: offset,
                  transform: `scale(${scale})`,
                  transformOrigin: 'center',
                  zIndex: 5 - layer,
                  opacity: 1 - layer * 0.12,
                }}
              >
                {isPrimary && url ? (
                  <img src={displayUrl} alt="主图" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                ) : isPrimary ? (
                  <div className="flex h-full flex-col items-center justify-center gap-1.5 text-xs text-light-muted">
                    {generating ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                        <span>生成中 {doneCount}/{count}</span>
                      </>
                    ) : (
                      <span>{count} 张图片</span>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
          {/* 主图标记 */}
          <div className="absolute bottom-2 right-2 z-10 rounded-full bg-blue-500 px-2 py-0.5 text-[10px] font-medium text-white shadow-sm">
            {count}
          </div>
        </div>
        )}
      </div>
      )}

      {data.modelLabel && (
        <p className="mt-2 flex items-center gap-1 text-[10px] text-slate-400">
          <span className="truncate">{data.modelLabel}</span>
        </p>
      )}

      {/* 全屏灯箱：翻页预览组内图片 */}
      {previewIndex !== null && lightboxImages.length > 0 && (
        <ImageLightbox
          images={lightboxImages}
          index={Math.min(previewIndex, lightboxImages.length - 1)}
          onIndexChange={setPreviewIndex}
          onClose={() => setPreviewIndex(null)}
          onDownload={(img, pos) => void downloadImage(img.url, viewableIndices[pos] ?? pos)}
        />
      )}
    </NodeWrapper>
  );
}
